import { Component, createRef } from 'preact';
import { Sparkles } from 'lucide-react';
import DeckCardTile from './DeckCardTile';
import CardGrid from './CardGrid';
import CardFilterBar from './CardFilterBar';
import RuneSpinner from './RuneSpinner';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones';
import { purchaseCardWithShards } from '../utils/arena/matchmakingApi';
import { shardPriceForRarity } from '../utils/arena/profileDefaults';
import { slotRarityForCard } from '../utils/sorcery/slotRarityOverrides';
import { buildOwnedMap } from '../utils/arena/collectionUtils';
import {
  getSkillState,
  getInscriptionDiscountPercent,
  applyInscriptionDiscount,
  canCraftUniques,
} from '../utils/arena/skills';
import { playUI, UI } from '../utils/arena/uiSounds';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  PANEL_BG, BEVELED_BTN, TAB_ACTIVE_TEXT, TAB_INACTIVE, TAB_BAR_STYLE,
  FourCorners, SECTION_HEADER_STYLE,
} from '../lib/medievalTheme';
import { TabBar } from './ui/TabBar';
import VikingOrnament from './VikingOrnament';
import CardInspector from './CardInspector';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { ShardIcon } from './ui/icons';
import { t } from '../localization/t';

const SHARD_COLOR = '#7dd3fc';

const RARITY_COLORS = {
  Ordinary: TEXT_MUTED,
  Exceptional: '#6ea8d4',
  Elite: '#b480d4',
  Unique: ACCENT_GOLD,
  Avatar: '#c45050',
};

const RARITY_ORDER = { Ordinary: 0, Exceptional: 1, Elite: 2, Unique: 3 };

// Rarities that Spellcraft can craft. Uniques are excluded by
// default. They're booster/draft rewards. The Inscription tier-5
// node "Inscriber of Wonders" (spellcraft_5) unlocks them; the
// `canCraftUniques(skills)` check below adds them to the list.
const SPELLCRAFT_RARITIES_BASE = ['Ordinary', 'Exceptional', 'Elite'];

export function getCraftableRarities(skills) {
  return canCraftUniques(skills)
    ? [...SPELLCRAFT_RARITIES_BASE, 'Unique']
    : SPELLCRAFT_RARITIES_BASE;
}

// Cards the server refuses to craft (CRAFT_BLOCKED_CARD_IDS in
// valkenhall-server/src/utils/cardIndex.js). Mirrored so the store
// doesn't list a card whose purchase would 404.
const CRAFT_BLOCKED_CARD_IDS = new Set(['sorcery-erik_s_curiosa']);

// Filter the full card catalog down to cards purchasable with shards:
// non-Avatar, rarity-allowed, with at least one non-foil printing.
// `skills` opens up Unique-rarity cards when the Inscription tier-5
// node has been invested.
function getPurchasableCards(sorceryCards, skills) {
  if (!Array.isArray(sorceryCards)) return [];
  const allowed = new Set(getCraftableRarities(skills));
  return sorceryCards.filter((card) => {
    if (!card?._sorcery) return false;
    if (card.type === 'Avatar') return false;
    if (CRAFT_BLOCKED_CARD_IDS.has(card.unique_id)) return false;
    if (!allowed.has(card.rarity)) return false;
    if (!shardPriceForRarity(slotRarityForCard(card))) return false;
    // Must have at least one non-foil printing so "buy a non-foil copy" is meaningful.
    return (card.printings || []).some((p) => !isFoilFinish(p.foiling));
  });
}

function pickStandardPrinting(card) {
  return (card.printings || []).find((p) => !isFoilFinish(p.foiling)) || card.printings?.[0] || null;
}

export default class StoreSinglesTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Filter predicate comes from CardFilterBar; defaults pass-through
      // until the first onChange fires on mount.
      matchesCard: () => true,
      filterKey: 0,
      sortBy: 'rarity',
      selectedCardId: null,
      purchasing: false,
      purchaseError: null,
      purchaseFlash: null,
      hoveredCard: null,
      inspectedEntry: null,
    };
    this.gridRef = createRef();
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleInspectorKeyDown);
    document.addEventListener('keyup', this.handleInspectorKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleInspectorKeyDown);
    document.removeEventListener('keyup', this.handleInspectorKeyUp);
    removeDismiss(this._escDismissId);
  }

  // Space toggles the full-screen inspector for whichever card is currently
  // hovered in the grid (matches the deck editor / auction house behavior).
  // Escape close is handled by the central esc-dismiss stack. Typing into a
  // search/filter input is ignored so the user can still hit space inside it.
  handleInspectorKeyDown = (e) => {
    const tag = e.target?.tagName;
    const editable = e.target?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    if ((e.key === ' ' || e.code === 'Space') && !editable) {
      e.preventDefault();
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) return;
      if (this.state.inspectedEntry) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredCard) {
        this.inspectorHold.noteOpen();
        this.setState({ inspectedEntry: this.state.hoveredCard });
      } else {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
      return;
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleInspectorKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    this.setState({ inspectedEntry: null });
  };

  componentDidUpdate(_prevProps, prevState) {
    // Jump the grid back to the top whenever the filtered set could have
    // changed. CardGrid owns the scroll container, so we drive it imperatively.
    if (
      prevState.filterKey !== this.state.filterKey ||
      prevState.sortBy !== this.state.sortBy
    ) {
      this.gridRef.current?.resetScroll();
    }

    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.hoveredCard &&
      this.state.hoveredCard !== prevState.hoveredCard &&
      this.inspectorHold.consumePending()
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedEntry: this.state.hoveredCard });
    }

    const inspectorOpen = this.state.inspectedEntry != null;
    const wasInspectorOpen = prevState.inspectedEntry != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => this.setState({ inspectedEntry: null }));
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  handleFilterChange = ({ matchesCard }) => {
    this.setState((s) => ({ matchesCard, filterKey: s.filterKey + 1 }));
  };

  handleSelectCard = (cardId) => {
    playUI(UI.SELECT);
    this.setState({ selectedCardId: cardId, purchaseError: null });
  };

  handlePurchase = async () => {
    const { selectedCardId } = this.state;
    const { sorceryCards, profile, onProfileUpdate } = this.props;
    if (!selectedCardId) return;
    const card = sorceryCards.find((c) => c.unique_id === selectedCardId);
    if (!card) return;
    const basePrice = shardPriceForRarity(slotRarityForCard(card));
    if (!basePrice) return;
    const price = applyInscriptionDiscount(basePrice, getSkillState());
    if ((profile.arcanaShards || 0) < price) return;

    this.setState({ purchasing: true, purchaseError: null });
    try {
      const result = await purchaseCardWithShards({ cardId: card.unique_id });
      playUI(UI.GOLD);
      // The server deducted the shards and added the card atomically;
      // sync the client profile to the returned totals and locally
      // bump the collection for immediate feedback. The local entry
      // uses the preferred standard printing for display purposes.
      // the server's collection storage is printing-agnostic, so this
      // is purely a client-side display choice.
      const printing = pickStandardPrinting(card);
      if (onProfileUpdate) {
        onProfileUpdate({
          arcanaShards: result.newTotals?.arcanaShards ?? ((profile.arcanaShards || 0) - price),
          collectionDelta: {
            cardId: card.unique_id,
            printingId: printing?.unique_id,
            quantity: 1,
          },
        });
      }
      this.setState({ purchasing: false, purchaseFlash: card.name });
      setTimeout(() => this.setState({ purchaseFlash: null }), 2000);
    } catch (err) {
      console.error('[StoreSinglesTab] purchase failed:', err);
      this.setState({
        purchasing: false,
        purchaseError: err?.message || t('Purchase failed. Please try again.'),
      });
    }
  };

  getItemKey = (card) => card.unique_id;

  // Foil descriptor for CardGrid. The singles tab only ever offers the
  // standard printing (getPurchasableCards requires a non-foil printing and
  // pickStandardPrinting prefers it), so this is effectively always null.
  // but we mirror the canonical wiring so a foil-only future printing would
  // render through the shared scene rather than a per-tile FoilCanvas.
  getItemFoil = (card) => {
    const printing = pickStandardPrinting(card);
    if (!printing || !isFoilFinish(printing.foiling)) return null;
    return { imageUrl: printing.image_url || '', zones: foilZonesForCard(card) };
  };

  renderTile = (card, { imageHidden, onTilt }) => {
    const printing = pickStandardPrinting(card);
    const isSelected = this.state.selectedCardId === card.unique_id;
    return (
      <div
        className="cursor-pointer"
        onClick={() => this.handleSelectCard(card.unique_id)}
      >
        {printing ? (
          <DeckCardTile
            entry={{ card, printing, zone: 'spellbook', entryIndex: 0 }}
            isSelected={isSelected}
            imageHidden={imageHidden}
            onTilt={onTilt}
            onClick={() => this.handleSelectCard(card.unique_id)}
            onHoverChange={(hovered) => this.setState({ hoveredCard: hovered ? { card, printing } : null })}
          />
        ) : (
          <div className="w-full rounded-card aspect-[63/88] flex items-center justify-center text-[0.625cqw]" style={{ background: `${GOLD} 0.04)`, color: TEXT_MUTED, border: `1px solid ${GOLD} 0.12)` }}>
            {card.name}
          </div>
        )}
      </div>
    );
  };

  renderFooter = (card) => {
    const { inscriptionDiscount, skillState } = this._footerCtx;
    const basePrice = shardPriceForRarity(slotRarityForCard(card));
    const price = applyInscriptionDiscount(basePrice, skillState);
    const owned = this._footerCtx.ownedMap.get(card.unique_id) || 0;
    return (
      <>
        {owned > 0 ? (
          <div className="text-center mt-1 text-[0.5625cqw] font-bold" style={{ color: ACCENT_GOLD }}>
            ×{owned}
          </div>
        ) : null}
        <div className="text-xs font-semibold truncate mt-1 text-center" style={{ color: TEXT_PRIMARY }}>
          {card.name}
        </div>
        <div className="flex items-center justify-center gap-1 mt-0.5">
          <ShardIcon size={11} />
          {inscriptionDiscount > 0 && price !== basePrice ? (
            <>
              <span className="text-[0.625cqw] tabular-nums line-through" style={{ color: TEXT_MUTED }}>{basePrice}</span>
              <span className="text-xs font-bold tabular-nums" style={{ color: SHARD_COLOR }}>{price}</span>
            </>
          ) : (
            <span className="text-xs font-bold tabular-nums" style={{ color: SHARD_COLOR }}>{price}</span>
          )}
        </div>
      </>
    );
  };

  render() {
    const { sorceryCards, profile } = this.props;
    const {
      matchesCard, sortBy, selectedCardId,
      purchasing, purchaseError, purchaseFlash,
    } = this.state;

    const skillState = getSkillState();
    const inscriptionDiscount = getInscriptionDiscountPercent(skillState);
    const purchasable = getPurchasableCards(sorceryCards, skillState);
    const ownedMap = buildOwnedMap(profile?.collection || []);

    let filtered = purchasable.filter(matchesCard);

    // Sort
    if (sortBy === 'rarity') {
      filtered = [...filtered].sort((a, b) => {
        const ra = RARITY_ORDER[a.rarity] ?? 99;
        const rb = RARITY_ORDER[b.rarity] ?? 99;
        if (ra !== rb) return ra - rb;
        return a.name.localeCompare(b.name);
      });
    } else if (sortBy === 'price') {
      filtered = [...filtered].sort((a, b) => (shardPriceForRarity(slotRarityForCard(a)) || 0) - (shardPriceForRarity(slotRarityForCard(b)) || 0));
    } else if (sortBy === 'name') {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }

    const selectedCard = selectedCardId ? sorceryCards.find((c) => c.unique_id === selectedCardId) : null;
    const selectedPrinting = selectedCard ? pickStandardPrinting(selectedCard) : null;
    const selectedBasePrice = selectedCard ? shardPriceForRarity(slotRarityForCard(selectedCard)) : null;
    const selectedPrice = selectedBasePrice != null ? applyInscriptionDiscount(selectedBasePrice, skillState) : null;
    const shardBalance = profile?.arcanaShards || 0;
    const canAfford = selectedPrice != null && shardBalance >= selectedPrice;
    const ownedCount = selectedCard ? (ownedMap.get(selectedCard.unique_id) || 0) : 0;

    // Context the CardGrid footer callback reads (price tier, ownership,
    // inscription discount). Stashed per-render so renderFooter stays a stable
    // class method (CardGrid keeps the foil scene mounted across renders).
    this._footerCtx = { ownedMap, inscriptionDiscount, skillState };

    return (
      <div className="flex gap-0 flex-1 min-h-0">
        {/* Left column: Filters + Card Grid */}
        <div className="flex-[65] flex flex-col min-h-0 pr-4">
          <CardFilterBar
            className="mb-3"
            sorceryCards={sorceryCards}
            features={{ scope: false, finish: false, type: true }}
            rarityOptions={getCraftableRarities(skillState)}
            onChange={this.handleFilterChange}
            trailing={(
              <TabBar className="flex items-center" style={TAB_BAR_STYLE} activeIndex={['price', 'rarity', 'name'].indexOf(sortBy)}>
                {[['price', t('Price')], ['rarity', t('Rarity')], ['name', t('Name')]].map(([key, label]) => (
                  <button
                    key={key}
                    data-tab-item
                    type="button"
                    className="px-3 py-1.5 text-[0.6875cqw] font-medium cursor-pointer capitalize"
                    style={sortBy === key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
                    onClick={() => this.setState({ sortBy: key })}
                  >
                    {label}
                  </button>
                ))}
              </TabBar>
            )}
          />

          {/* Card grid. CardGrid owns its own scroll container + virtualization
              and renders foil printings through the shared WebGL scene. Kept
              mounted (empty state overlays it) so the foil context isn't torn
              down on filter changes. */}
          <div className="flex-1 flex min-h-0 relative">
            <CardGrid
              ref={this.gridRef}
              items={filtered}
              getItemKey={this.getItemKey}
              getItemFoil={this.getItemFoil}
              renderTile={this.renderTile}
              renderFooter={this.renderFooter}
              measureFooter={(
                <>
                  <div className="text-center mt-1" style={{ fontSize: '0.5625cqw' }}>&nbsp;</div>
                  <div className="text-xs mt-1" style={{ lineHeight: 1.3 }}>&nbsp;</div>
                  <div className="mt-0.5" style={{ height: '1cqw' }} />
                </>
              )}
            />
            {filtered.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm" style={{ color: TEXT_MUTED }}>{t('No cards match your filters')}</div>
            ) : null}
          </div>
        </div>

        {/* Right column: Card Preview + Buy action */}
        <div className="flex-[35] min-h-0 overflow-hidden">
          <div className="relative p-4 h-full flex flex-col" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, borderRadius: 'var(--radius-lg)', isolation: 'isolate' }}>
            <FourCorners />
            <VikingOrnament ornament="style1007" variant="centerpiece" opacity={0.08} />
            {selectedCard && selectedPrinting ? (
              <div className="flex flex-col items-center gap-3 h-full min-h-0">
                <img
                  src={selectedPrinting.image_url}
                  alt={selectedCard.name}
                  className="rounded-card object-cover bg-black/40 shrink min-h-0"
                  style={{ aspectRatio: '63 / 88', maxWidth: '60%' }}
                />
                <div className="w-full text-center shrink-0">
                  <div className="text-sm font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                    {selectedCard.name}
                  </div>
                  <div className="text-[0.6875cqw] font-semibold mt-0.5" style={{ color: RARITY_COLORS[selectedCard.rarity] || TEXT_MUTED }}>
                    {selectedCard.rarity}
                  </div>
                  {ownedCount > 0 ? (
                    <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>
                      {t('You own:')} <span style={{ color: ACCENT_GOLD }}>{ownedCount}</span>
                    </div>
                  ) : (
                    <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>{t("You don't own this card")}</div>
                  )}
                </div>

                <div className="w-full shrink-0 flex flex-col gap-2 mt-auto">
                  <div className="flex items-center justify-center gap-2 py-2 rounded" style={{ background: 'rgba(0,0,0,0.35)', border: `1px solid ${GOLD} 0.12)` }}>
                    <ShardIcon size={16} />
                    {inscriptionDiscount > 0 && selectedBasePrice !== selectedPrice ? (
                      <>
                        <span className="text-sm tabular-nums line-through" style={{ color: TEXT_MUTED }}>{selectedBasePrice}</span>
                        <span className="text-xl font-bold tabular-nums" style={{ color: SHARD_COLOR }}>{selectedPrice}</span>
                      </>
                    ) : (
                      <span className="text-xl font-bold tabular-nums" style={{ color: SHARD_COLOR }}>{selectedPrice}</span>
                    )}
                    <span className="text-[0.625cqw]" style={{ color: 'rgba(125,211,252,0.5)' }}>{t('arcana')}</span>
                  </div>
                  {inscriptionDiscount > 0 ? (
                    <div className="text-[0.625cqw] text-center" style={{ color: ACCENT_GOLD }}>
                      {t('Inscription discount: −{n}%', { n: inscriptionDiscount })}
                    </div>
                  ) : null}

                  <div className="text-[0.625cqw] text-center" style={{ color: TEXT_MUTED }}>
                    {t('Balance:')} <span style={{ color: canAfford ? SHARD_COLOR : '#c45050' }}>{shardBalance}</span> {t('arcana')}
                  </div>

                  {purchaseError ? (
                    <div className="text-[0.625cqw] text-center" style={{ color: '#c45050' }}>{purchaseError}</div>
                  ) : null}
                  {purchaseFlash ? (
                    <div className="text-[0.6875cqw] text-center font-semibold" style={{ color: ACCENT_GOLD }}>
                      {t('+1 {name} added!', { name: purchaseFlash })}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    disabled={!canAfford || purchasing}
                    className="w-full py-2.5 text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
                    style={{
                      ...BEVELED_BTN,
                      background: canAfford ? `linear-gradient(180deg, rgba(125,211,252,0.18), rgba(125,211,252,0.08))` : `rgba(20,16,10,0.7)`,
                      border: `1px solid ${canAfford ? 'rgba(125,211,252,0.5)' : 'rgba(166,160,155,0.2)'}`,
                      borderRadius: 'var(--radius-sm)',
                      color: canAfford ? SHARD_COLOR : 'rgba(166,160,155,0.4)',
                    }}
                    onClick={this.handlePurchase}
                  >
                    {purchasing ? (
                      <span className="flex items-center justify-center gap-2"><RuneSpinner size={14} /> {t('Purchasing...')}</span>
                    ) : !canAfford ? (
                      t('Need {n} more arcana', { n: selectedPrice - shardBalance })
                    ) : (
                      <span className="flex items-center justify-center gap-1.5">
                        <Sparkles style={{ width: "0.75cqw", height: "0.75cqw" }} /> {t('Buy with Arcana')}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {this.state.inspectedEntry ? (
          <CardInspector
            card={this.state.inspectedEntry.card}
            imageUrl={this.state.inspectedEntry.printing?.image_url}
            rarity={this.state.inspectedEntry.card?.rarity}
            foiling={this.state.inspectedEntry.printing?.foiling}
            onClose={() => this.setState({ inspectedEntry: null })}
          />
        ) : null}
      </div>
    );
  }
}
